import { useEffect, useState } from "react";
import http from "@/lib/api";
import { Panel, Empty, fmt } from "./AdminKit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Ban } from "lucide-react";

const empty = { category: "general_service", question: "", answer: "", text_ru: "", audience: "public", status: "draft", valid_until: "" };
const categories = ["general_service", "pricing", "process", "repair_status", "workshop", "capsule_machines", "missing_components"];
const today = () => new Date().toISOString().slice(0, 10);
const AUD = { public: "Для клиентов", internal: "Внутренняя инструкция AI" };
export const kbState = (r) => {
  if (r.status === "cancelled") return ["Отменено", "bg-slate-700 text-slate-400"];
  if (r.status === "draft") return ["Черновик", "bg-amber-900/40 text-amber-300"];
  if (r.valid_until && r.valid_until < today()) return ["Истекло", "bg-red-900/40 text-red-300"];
  return ["Активно · в веб-чате", "bg-emerald-900/40 text-emerald-300"];
};
const sel = "mt-1 w-full bg-slate-700 border border-slate-600 text-white rounded px-2 py-2 text-sm";

function KbDialog({ open, setOpen, initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [back, setBack] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(initial); setBack(""); }, [initial]);
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const prepare = async () => {
    setBusy(true);
    try { const r = await http.post("/v1/admin/knowledge-base/prepare", { text_ru: form.text_ru, audience: form.audience }); setForm({ ...form, question: r.data.question, answer: r.data.answer }); setBack(r.data.back_translation_ru); }
    catch (e) { toast.error(e?.response?.data?.detail || "Ошибка"); } finally { setBusy(false); }
  };
  const recheck = async () => {
    setBusy(true);
    try { setBack((await http.post("/v1/admin/back-translate", { text: `${form.question}\n${form.answer}`, language: "no" })).data.back_translation_ru); }
    catch { toast.error("Ошибка"); } finally { setBusy(false); }
  };
  const save = async (status) => {
    if (status === "active" && !window.confirm("Подтвердить и активировать? Vadim сразу начнёт использовать эту информацию в веб-чате.")) return;
    setBusy(true);
    try {
      const body = { ...form, status };
      if (form.id) await http.put(`/v1/admin/knowledge-base/${form.id}`, body); else await http.post("/v1/admin/knowledge-base", body);
      toast.success(status === "active" ? "Активировано" : "Черновик сохранён"); setOpen(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ошибка"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kb-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Изменить запись" : "Передать информацию AI"}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><label className="text-slate-300">Тип</label><select value={form.audience} onChange={f("audience")} className={sel} data-testid="kb-audience">{Object.entries(AUD).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <div><label className="text-slate-300">Категория</label><select value={form.category} onChange={f("category")} className={sel}>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className="text-slate-300">Действует до (необяз.)</label><Input type="date" value={form.valid_until || ""} onChange={f("valid_until")} className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="kb-valid-until" /></div>
          </div>
          <p className="text-xs text-slate-400">{form.audience === "internal" ? "Внутренняя инструкция: Vadim следует ей, но не цитирует клиенту. Не может отменить правила безопасности и проверки личности." : "Для клиентов: Vadim может использовать этот ответ в разговоре с любым клиентом."} Конфиденциальные заметки сюда не пишите — для них есть внутренние заметки в карточке ремонта.</p>
          <div><label className="text-slate-300">Ваш текст (RU)</label><Textarea value={form.text_ru} onChange={f("text_ru")} rows={4} className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="kb-text-ru" /></div>
          <Button type="button" disabled={busy || !form.text_ru.trim()} onClick={prepare} className="bg-blue-700 hover:bg-blue-600 h-8 text-xs" data-testid="kb-prepare">{busy ? "…" : "Подготовить с AI (норвежский)"}</Button>
          <div><label className="text-slate-300">Тема / вопрос (NO)</label><Input value={form.question} onChange={(e) => { f("question")(e); setBack(""); }} className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="kb-question" /></div>
          <div><label className="text-slate-300">Текст для AI (NO)</label><Textarea value={form.answer} onChange={(e) => { f("answer")(e); setBack(""); }} rows={5} className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="kb-answer" /></div>
          <div className="rounded border border-slate-600 bg-slate-900/50 p-2">
            <div className="text-xs text-slate-400 mb-1">Контрольный перевод (RU) — сравните с вашим текстом:</div>
            {back ? <div className="text-xs text-slate-200 whitespace-pre-wrap" data-testid="kb-back">{back}</div>
              : <Button type="button" disabled={busy || !form.answer.trim()} onClick={recheck} className="bg-slate-600 hover:bg-slate-500 h-7 text-xs" data-testid="kb-recheck">Показать контрольный перевод</Button>}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button disabled={busy} variant="secondary" onClick={() => save("draft")} data-testid="kb-save-draft">Сохранить черновик</Button>
          <Button disabled={busy || !back} className="bg-emerald-700 hover:bg-emerald-600 text-white" onClick={() => save("active")} data-testid="kb-activate">Подтвердить и активировать</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminKnowledgeBase() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState(empty);
  const load = () => http.get("/v1/admin/knowledge-base").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const edit = (r) => { setInitial({ ...empty, ...r, text_ru: r.text_ru || "", valid_until: r.valid_until || "", status: r.status || "active", audience: r.audience || "public" }); setOpen(true); };
  const cancel = async (r) => {
    if (!window.confirm("Отменить запись? Vadim перестанет её использовать (запись сохранится в истории).")) return;
    try { await http.put(`/v1/admin/knowledge-base/${r.id}`, { ...empty, ...r, text_ru: r.text_ru || "", valid_until: r.valid_until || "", audience: r.audience || "public", status: "cancelled" }); toast.success("Отменено"); load(); }
    catch { toast.error("Ошибка"); }
  };
  return (
    <div className="space-y-6" data-testid="admin-knowledge-base">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-white">База знаний</h1>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={() => { setInitial({ ...empty }); setOpen(true); }} data-testid="kb-new"><Plus className="h-4 w-4" />Передать информацию AI</Button>
      </div>
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-slate-400 text-sm" data-testid="kb-info">
        Активные записи сразу используются текстовым Vadim (веб-чат). Голосовой Vadim (ElevenLabs) получит их только после подключения инструментов — фактический статус см. на странице «AI-агент Vadim».
        Информация по конкретному ремонту — через «Коммуникация» в заказе.
      </div>
      <Panel title={`Всего: ${rows.length}`}>
        {rows.length === 0 ? <Empty /> : (
          <div className="space-y-3">
            {rows.map((r) => { const [label, cls] = kbState(r); return (
              <div key={r.id} className="rounded-lg border border-slate-700 bg-slate-700/30 p-3" data-testid={`kb-row-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className={`px-2 py-0.5 rounded-full ${cls}`} data-testid={`kb-state-${r.id}`}>{label}</span>
                    <span className="text-slate-300">{AUD[r.audience || "public"]}</span>
                    <span className="text-slate-500">{r.category}{r.valid_until ? ` · до ${r.valid_until}` : ""}{r.approved_at ? ` · подтверждено ${fmt(r.approved_at)}` : ""}{r.history?.length ? ` · изменений: ${r.history.length}` : ""}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => edit(r)} className="text-blue-300 hover:text-blue-200" data-testid={`kb-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
                    {r.status !== "cancelled" && <button onClick={() => cancel(r)} className="text-red-400 hover:text-red-300" data-testid={`kb-cancel-${r.id}`}><Ban className="h-4 w-4" /></button>}
                  </div>
                </div>
                <div className="text-sm text-white">{r.question}</div>
                <div className="text-sm text-slate-300 whitespace-pre-wrap">{r.answer}</div>
                {r.text_ru && <div className="text-xs text-slate-500 mt-1 whitespace-pre-wrap">RU: {r.text_ru}</div>}
              </div>
            ); })}
          </div>
        )}
      </Panel>
      <KbDialog open={open} setOpen={setOpen} initial={initial} onSaved={load} />
    </div>
  );
}
